/**
 * Route registry — the single source of truth for all BuildOS screen routes.
 *
 * Other agents (#36–#39) add their fully-implemented screens here WITHOUT
 * touching App.tsx. The registry is consumed by <AppShell> to render the
 * sidebar nav links and by the React Router route tree.
 *
 * Usage: import { ROUTES, type RouteDefinition } from './registry'
 */

import React from "react";
import { TodayPage } from "../pages/TodayPage.js";
import { ProjectsPage } from "../pages/ProjectsPage.js";
import { ProjectDetailPage } from "../pages/ProjectDetailPage.js";
import { DealsPage } from "../pages/DealsPage.js";
import { BrainPage } from "../pages/BrainPage.js";
import { SettingsPage } from "../pages/SettingsPage.js";

export interface RouteDefinition {
  /** Unique key — matches the BuildOSScreen type from ai-kit/stub */
  key: string;
  /** Display label in the sidebar nav */
  label: string;
  /** React Router path (relative, used inside the shell layout) */
  path: string;
  /** The page component to render */
  element: React.ReactElement;
  /** Whether this route should appear in the primary sidebar navigation */
  showInNav: boolean;
  /** Optional icon name (resolved in the sidebar component) */
  icon?: string;
}

/**
 * Primary route registry.
 *
 * To add a new screen:
 *  1. Create src/pages/MyPage.tsx
 *  2. Import it here
 *  3. Add a RouteDefinition entry below
 *  4. That's it — App.tsx and AppShell pick it up automatically.
 */
export const ROUTES: RouteDefinition[] = [
  {
    key: "today",
    label: "Today",
    path: "/",
    element: <TodayPage />,
    showInNav: true,
    icon: "today",
  },
  {
    key: "projects",
    label: "Projects",
    path: "/projects",
    element: <ProjectsPage />,
    showInNav: true,
    icon: "projects",
  },
  {
    key: "deals",
    label: "Deals",
    path: "/deals",
    element: <DealsPage />,
    showInNav: true,
    icon: "deals",
  },
  {
    key: "brain",
    label: "Brain",
    path: "/brain",
    element: <BrainPage />,
    showInNav: true,
    icon: "brain",
  },
  {
    key: "settings",
    label: "Settings",
    path: "/settings",
    element: <SettingsPage />,
    showInNav: true,
    icon: "settings",
  },
  {
    key: "project-detail",
    label: "Project Detail",
    path: "/projects/:id",
    element: <ProjectDetailPage />,
    showInNav: false, // nested route — accessed from Projects list
    icon: "project-detail",
  },
];

/** Only the routes that appear in the sidebar */
export const NAV_ROUTES = ROUTES.filter((r) => r.showInNav);
